/**
 * One project under way — `client/projects.html` §1, the page's first region.
 *
 * P1: the region opens with its conclusion ("Milestone 3 of 4 … is under way"),
 * carries **one** figure at size, and puts everything else — the schedule, the
 * scope change, the handover rule and the quote — below it or behind the one
 * disclosure. The figure is a `HeroFigure` on the first live project and a
 * `SubFigure` on any after it, because CONTRACT.md §4.6 spends the `--fs-7` once
 * per page and a client with two live projects is not a reason to spend it twice.
 *
 * **The figure at size is progress, not money** — session 41 (C1). Ferit's brief for
 * the client view is "the actual primary goal is for them to see that we are working
 * on their stuff", and the page that opened on *"Still to invoice — $2,685.00"* led
 * with what the client owes rather than with what the studio has done. The hero is
 * now "2 of 4", the delivered count, which is a subtotal of the milestone table
 * directly under it (`DESIGN.md:17-21`) and reconciles by counting down its Delivered
 * column. "Still to invoice" keeps its figure and its whole explanation and becomes
 * one `SubFigure` line. This is a deliberate deviation from `.docs/19-client-portal-
 * pages.md:63`, which specified the hero as still to invoice; that file is not edited
 * and the deviation is in session 41's report.
 *
 * P4: nothing in here is ochre. A milestone that is merely *next* is not a
 * decision and does not cost more — `Step state="now"` is emphasised by weight
 * (CONTRACT.md §2.11), and that is the whole of the emphasis this region has.
 *
 * **The region reads its own detail.** `GET /api/clients/me/projects` answers a work
 * order and its milestones; the quote fold's build-up (`lines`, `pricing`) and the
 * project's `scope_changes` are on `GET /api/clients/me/projects/:ref`, one call per
 * project — which is why it is made here rather than on the page. Two consequences,
 * both deliberate:
 *
 *   - it is `inline`, so a `404` on one project's detail leaves the region drawn from
 *     the list payload rather than replacing the page with an error boundary;
 *   - the page does **not** wait for it. The region therefore paints once with the
 *     scope changes the requests ledger knows about and again with the ones the
 *     project itself carries. The two agree except where the ledger's first page has
 *     scrolled past an old scope change, which is exactly the case the detail read
 *     exists to fix — so the correction is the point rather than a flicker to hide.
 */

import { Link } from '@tanstack/react-router'
import {
  isZero,
  type ClientProjectMilestoneView,
  type InvoiceView,
  type Money,
  type WorkOrderView,
} from '@fareast/shared'

import { Fold } from '../../../components/portal/Disclosure.js'
import { Region, RegionHead } from '../../../components/portal/Region.js'
import { Row, ScrollX, Stack } from '../../../components/portal/Layout.js'
import { Tag } from '../../../components/portal/Status.js'
import { Step, Stepper } from '../../../components/portal/Stepper.js'
import { TBody, THead, Table, Td, Th, Tr } from '../../../components/portal/Table.js'
import {
  Answer,
  B,
  DateText,
  DayText,
  HeroCap,
  HeroFigure,
  Linkish,
  Mono,
  Muted,
  Num,
  Prose,
  Ref,
  Small,
  SubFigure,
} from '../../../components/portal/Text.js'
import { useProject } from '../../../api/client/index.js'
import { QuoteBuildUpRows } from '../../../components/client/QuoteBuildUp.js'
import {
  currentMilestone,
  deliveredMilestones,
  inWords,
  invoiceForMilestone,
  invoicedAndPaid,
  overdue,
  projectBreakdownOf,
  projectToDate,
  scopeChangeMilestone,
  scopeChangesOf,
  scopeStillToInvoice,
  stillToInvoice,
  type ProjectRow,
} from './facts.js'

/** The anchor `client/projects.html` and the requests page link to: `#wo-2026-008`. */
export function projectAnchor(ref: string): string {
  return ref.toLowerCase()
}

export type LiveProjectProps = {
  readonly project: ProjectRow
  readonly invoices: readonly InvoiceView[]
  /**
   * The scope changes the **requests ledger** knows about, already filtered. Used
   * until this region's own detail read lands, and for ever if it never does.
   */
  readonly scopeChanges: readonly WorkOrderView[]
  /** The rate card's name for `work_order.template`, or null. The head's tag. */
  readonly templateName: string | null
  /** Today, `YYYY-MM-DD`, in the viewer's zone. The page reads the clock once. */
  readonly today: string
  /** True for the first live project on the page — the one that gets the hero. */
  readonly lead: boolean
}

export function LiveProject({
  project,
  invoices,
  scopeChanges: fromLedger,
  templateName,
  today,
  lead,
}: LiveProjectProps) {
  const { work_order: order, milestones } = project
  const detail = useProject(order.ref, { inline: true }).data

  const scopeChanges =
    detail === undefined ? fromLedger : scopeChangesOf(detail.scope_changes, project)
  const current = currentMilestone(project)
  const still = stillToInvoice(project, scopeChanges)
  const paid = invoicedAndPaid(project, invoices, scopeChanges)
  const late = overdue(project, invoices, today)
  const final = scopeChangeMilestone(project)
  const scopeStill = scopeStillToInvoice(project, scopeChanges)
  /* "2 of 4" — the two numbers the table below prints, composed the way CONTRACT.md
     §4.6's own examples compose a figure out of a pair (`7 / 6`, `4 of 5`). */
  const progress = `${String(deliveredMilestones(project).length)} of ${String(milestones.length)}`

  return (
    <Region id={projectAnchor(order.ref)} aria-label={order.title}>
      <RegionHead
        title={order.title}
        note={
          <>
            <Ref value={order.ref} />
            {templateName === null ? null : (
              <>
                {' '}
                <Tag>{templateName}</Tag>
              </>
            )}
          </>
        }
      />

      <Answer>{progressSentence(project, invoices)}</Answer>

      <Stepper>
        {milestones.map((milestone) => (
          <Step
            key={milestone.seq}
            state={stepStateOf(milestone.delivered_at !== null, milestone.seq === current?.seq)}
          >
            {milestone.title}
          </Step>
        ))}
      </Stepper>

      {/*
        What the region leads with: how much of the project has landed, and on the
        lead project it is the page's one `--fs-7`. It is the delivered rows of the
        table below, counted — DESIGN.md's permitted derivation, and the only thing
        this region works out for itself beyond the money `facts.ts` owns.
      */}
      <Stack gap="tight">
        <HeroCap>Milestones delivered</HeroCap>
        {lead ? <HeroFigure value={progress} /> : <SubFigure value={progress} />}
      </Stack>

      {still === null ? null : (
        <Stack gap="tight">
          <Row>
            <HeroCap>Still to invoice</HeroCap>
            <SubFigure value={still} />
          </Row>
          {paid === null || late === null ? null : (
            <Small>
              <Muted>
                Invoiced and paid so far <Num value={paid} /> &middot; overdue{' '}
                <Num value={late} bad={!isZero(late)} />
              </Muted>
            </Small>
          )}
          {/*
            The reconciling line — session 24, R2-23.

            Without it the two figures above read as a partition of the fixed total the
            milestone table below them sums to, and they are not: an approved scope
            change is inside "Still to invoice" and inside nothing else on this fold.
            The same project therefore had one number here, another on the requests
            ledger and a third on the studio's own page, with no line anywhere saying
            which was which.

            It names the delta and **refers** to the fold for the build-up rather than
            restating the project to date beside it — DESIGN.md P2. Nothing prints when
            there is no scope change still to come, because then the two figures do
            partition the fixed total and there is nothing to explain.
          */}
          {scopeStill === null || isZero(scopeStill) ? null : (
            <Small>
              <Muted>
                Still to invoice includes <Num value={scopeStill} /> of scope approved since the
                quote.
              </Muted>
            </Small>
          )}
        </Stack>
      )}

      <ScrollX>
        <Table>
          <THead>
            <Tr>
              <Th scope="col">#</Th>
              <Th scope="col">Milestone</Th>
              <Th scope="col" right>
                %
              </Th>
              <Th scope="col" right>
                Amount
              </Th>
              <Th scope="col">Delivered</Th>
              <Th scope="col">Invoice</Th>
            </Tr>
          </THead>
          <TBody>
            {milestones.map((milestone) => {
              const invoice = invoiceForMilestone(invoices, project, milestone)
              return (
                <Tr key={milestone.seq}>
                  <Td>
                    <Mono>{String(milestone.seq)}</Mono>
                  </Td>
                  <Td>
                    <B>{milestone.title}</B>
                  </Td>
                  <Td right num>
                    <Num>{`${String(milestone.percent)}%`}</Num>
                  </Td>
                  <Td right num>
                    <Num value={milestone.amount} />
                  </Td>
                  <Td date>
                    <Schedule milestone={milestone} />
                  </Td>
                  <Td>
                    {invoice === null ? (
                      <Small>
                        <Muted>on delivery</Muted>
                      </Small>
                    ) : (
                      <Ref value={invoice.ref} />
                    )}
                  </Td>
                </Tr>
              )
            })}
          </TBody>
        </Table>
      </ScrollX>

      {/*
        The client's own brief, beside what it has bought so far — session 41 (C1). It
        was inside the quote fold, which is the one thing on this region a client opens
        to check a number rather than to read what they asked for; `DeliveredProject`
        has always printed it in the open, and now both do. The approval date stays on
        it because nothing else on the page prints `work_order.approved_at`, and the
        build-up it used to sit above is still folded.
      */}
      <Prose>
        {order.approved_at === null ? (
          <>Scope, as quoted: {order.scope}</>
        ) : (
          <>
            Scope, as approved on <DateText value={order.approved_at} />: {order.scope}
          </>
        )}
      </Prose>

      {scopeChanges.map((change, index) => (
        <Prose key={change.ref}>
          {scopeChanges.length === 1
            ? 'One scope change since approval'
            : `Scope change ${String(index + 1)} of ${String(scopeChanges.length)} since approval`}
          : <B>{change.title}</B>
          {change.total_amount === null ? null : (
            <>
              {' '}
              &mdash; <Num value={change.total_amount} />
            </>
          )}
          . Requested as <Ref value={change.ref} />
          {change.approved_at === null ? null : (
            <>
              {' '}
              and approved <DateText value={change.approved_at} />
            </>
          )}
          {change.approved_by_name === null ? null : <> by {change.approved_by_name}</>}
          {final === null ? null : (
            <>; it is billed on milestone {String(final.seq)}, with the rest of the project</>
          )}
          .
        </Prose>
      ))}

      <Prose>
        Handover files unlock in{' '}
        <Linkish asChild>
          <Link to="/app/files">Files</Link>
        </Linkish>{' '}
        when the final milestone invoice is paid.
      </Prose>

      {/*
        Session 41 (C1) renamed this. It read "Approved scope and the original quote"
        until the approved scope came out of it and up beside the milestone table —
        at which point the summary named a thing the fold no longer held, which is
        the same defect as a sentence promising a control that is not there. What is
        behind it now is the quote line by line, the scope approved since, and the
        project to date, and that is what it says.
      */}
      <Fold summary="The original quote, and the scope approved since">
        <Stack>
          {order.total_amount === null ? null : (
            <ScrollX>
              <Table>
                <THead>
                  <Tr>
                    <Th scope="col">Quote line</Th>
                    <Th scope="col" right>
                      Qty
                    </Th>
                    <Th scope="col" right>
                      Amount
                    </Th>
                  </Tr>
                </THead>
                <TBody>
                  {/*
                    The build-up when the detail has landed, and the total alone until
                    it does — a fold that opened on an empty table would be worse than
                    one that opens on the number the client agreed to.
                  */}
                  {detail === undefined ? (
                    <Tr variant="total">
                      <Td>Fixed total, as approved</Td>
                      <Td />
                      <Td right num>
                        <Num value={order.total_amount} lg />
                      </Td>
                    </Tr>
                  ) : (
                    <QuoteBuildUpRows
                      lines={detail.lines}
                      breakdown={projectBreakdownOf(detail.pricing)}
                      discountLabel="Signed with a retainer"
                      totalLabel="Fixed total, rounded to $10"
                      total={projectBreakdownOf(detail.pricing)?.total ?? order.total_amount}
                      emphasiseTotal
                    />
                  )}
                  {scopeChanges.map((change) => (
                    <Tr key={change.ref}>
                      <Td>
                        {change.title} &middot; <Ref value={change.ref} />
                      </Td>
                      <Td />
                      <Td right num>
                        {change.total_amount === null ? null : <Num value={change.total_amount} />}
                      </Td>
                    </Tr>
                  ))}
                  {scopeChanges.length === 0 ? null : (
                    <Tr variant="total">
                      <Td>Project to date</Td>
                      <Td />
                      <Td right num>
                        <ToDate project={project} scopeChanges={scopeChanges} />
                      </Td>
                    </Tr>
                  )}
                </TBody>
              </Table>
            </ScrollX>
          )}
        </Stack>
      </Fold>
    </Region>
  )
}

/* ------------------------------------------------------------------------ pieces */

/**
 * The quote, line by line, and the steps between the list and the fixed total —
 * `client/projects.html`'s fold, which is the five priced lines, the subtotal, the
 * retainer discount and the rounding. The subtotal *is* the tiered amount; it is not
 * labelled as one, because `QuoteBuildUp`'s `statesTier` is off for a client page
 * (`InvoiceDetailsPanel.tsx` §"The market tier is not one of them").
 *
 * Every row is a **stored field** of `pricing_breakdown` (`ProjectBreakdownSchema`),
 * never a figure this page computed, and the column reconciles by reading down it:
 * subtotal + discount + conversion + rounding = total. That is what DESIGN.md's
 * permitted derivation asks of a table like this one, and it is why the rounding is a
 * row of its own rather than folded into the discount line the way the mockup folds
 * it — the mockup's single `−$530.00` is `retainer_discount` (−$526.00) plus
 * `rounding` (−$4.00) and is not a number anything answers with.
 *
 * With no build-up — an unpriced quote, a work order priced from units, or a detail
 * read that has not landed or has failed — it falls back to the agreed total on its
 * own, which is the one figure the list payload does carry.
 */

function ToDate({
  project,
  scopeChanges,
}: {
  readonly project: ProjectRow
  readonly scopeChanges: readonly WorkOrderView[]
}) {
  const total: Money | null = projectToDate(project, scopeChanges)
  return total === null ? null : <Num value={total} lg />
}

/**
 * The Delivered column of one milestone row: the day it landed, or the day it is due.
 *
 * **Late reads as a word** — "late, now due 11 Sep 2026" — off the `late` the server
 * sets (`readmodels/client-projects.ts`: `due_on` before today in the *studio's* zone
 * and nothing delivered). Not an icon, because `mockups/assets/CONTRACT.md` §5 refuses
 * an icon family outright; not ochre, because `DESIGN.md` P4 keeps ochre for a
 * decision the reader can take and a milestone the studio owes is not one; and not a
 * `.step` or a `.pill` either, because §4 DON'T 3 has no use for a chip whose word the
 * cell beside it already says.
 *
 * **A milestone with no date prints nothing** — session 41 (T4). It used to print
 * "not scheduled", which is a placeholder saying the same thing as an empty cell in
 * more words. The cell keeps its `date` class either way, so the column still aligns.
 */
function Schedule({ milestone }: { readonly milestone: ClientProjectMilestoneView }) {
  if (milestone.delivered_at !== null) return <DateText value={milestone.delivered_at} />
  if (milestone.due_on === null) return null
  return (
    <Muted>
      {milestone.late ? 'late, now due ' : 'due '}
      <DayText value={milestone.due_on} />
    </Muted>
  )
}

/** `done` for a delivered milestone, `now` for the one being worked on. */
function stepStateOf(delivered: boolean, current: boolean): 'done' | 'now' | undefined {
  if (delivered) return 'done'
  return current ? 'now' : undefined
}

/**
 * The region's answer — `client/projects.html`: "Milestone 3 of 4, **build and
 * content**, is under way. Milestones 1 and 2 are delivered and paid."
 *
 * "and paid" is only written when every one of their invoices has settled; a
 * milestone that is delivered and still owed reads "delivered and invoiced", which
 * is the honest half of the same sentence.
 */
function progressSentence(project: ProjectRow, invoices: readonly InvoiceView[]): string {
  const current = currentMilestone(project)
  const done = deliveredMilestones(project)
  const count = project.milestones.length

  const first =
    current === null
      ? 'Every milestone is delivered.'
      : `Milestone ${String(current.seq)} of ${String(count)}, ${current.title}, is under way.`

  if (done.length === 0) return first

  const settled = done.every((milestone) => {
    const invoice = invoiceForMilestone(invoices, project, milestone)
    return invoice !== null && invoice.paid_at !== null
  })
  const numbers = inWords(done.map((milestone) => String(milestone.seq)))
  const noun = done.length === 1 ? 'Milestone' : 'Milestones'
  const verb = done.length === 1 ? 'is' : 'are'
  const state = settled ? 'delivered and paid' : 'delivered'

  return `${first} ${noun} ${numbers} ${verb} ${state}.`
}
